/**
 * `/logout` (parity card **P-038**).
 *
 * monday's own URL form, and the destination of every migrated bookmark that carries it, so it
 * has to be a real route rather than the shell's `*` NotFound — which is exactly what it hit
 * before: "We couldn't find the content you were looking for", with the session still alive.
 *
 * It lives **outside** `<AppShell/>`, like the other auth screens: the shell's three bootstrap
 * queries must not run for a session that is being revoked. The screen itself is one line of
 * copy — it is on screen for the length of one mutation — and it never renders a signed-in
 * surface, so a slow api cannot flash the app at somebody who has just signed out.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthShell } from './AuthShell';
import { performLogout } from '../../modules/shell/logout';
import { useTRPCClient } from '../../lib/trpc';

export function Logout() {
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  /** StrictMode mounts every effect twice in development; the session is revoked once. */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void performLogout(client, queryClient);
  }, [client, queryClient]);

  return (
    <AuthShell title="Signing you out" testId="logout-screen">
      <p data-testid="logout-message">One moment — we are ending your session.</p>
    </AuthShell>
  );
}
